// AuthGate — the shell everything else renders inside.
//
// Three visible states:
//   1. Loading — first Firebase state hasn't arrived yet
//   2. Signed out — Google sign-in button
//   3. Signed in but not a Squint team member — 403 message with sign-out
//   4. Signed in + team member — renders children
import { useState } from "react";
import { GoogleAuthProvider, signInWithPopup, signOut } from "firebase/auth";
import { auth } from "./firebase";
import { useUser } from "./useUser";
import { LoadingLine } from "@/components/LoadingLine";
import { Button } from "@/ui/button";

export function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, isViewer, isLoading, isSignedIn, error } = useUser();
  const [signInErr, setSignInErr] = useState<string>("");
  const [signingIn, setSigningIn] = useState(false);

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <LoadingLine label="Verifying you" />
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-5 bg-bg">
        <h1 className="font-serif text-[34px] font-semibold tracking-tight">
          Squint Release Hub
        </h1>
        <p className="text-text-secondary">Sign in with your Squint Google account.</p>
        <Button
          disabled={signingIn}
          onClick={async () => {
            setSignInErr("");
            setSigningIn(true);
            try {
              const provider = new GoogleAuthProvider();
              provider.setCustomParameters({ prompt: "select_account" });
              await signInWithPopup(auth, provider);
            } catch (e) {
              setSignInErr(e instanceof Error ? e.message : String(e));
            } finally {
              setSigningIn(false);
            }
          }}
        >
          {signingIn ? "Signing in…" : "Sign in with Google"}
        </Button>
        {signInErr && (
          <p className="text-sm text-destructive">{signInErr}</p>
        )}
      </div>
    );
  }

  // /api/me errored — distinct from "user is not a team member". Show the
  // actual failure so 500s (bad env, squapi down) don't disguise as 403s.
  if (error) {
    const message = error instanceof Error ? error.message : String(error);
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-5 bg-bg px-6 text-center">
        <h1 className="font-serif text-[28px] font-semibold">Something went wrong.</h1>
        <p className="text-text-secondary">
          Couldn't reach <code>/api/me</code>. Check the server logs.
        </p>
        <p className="max-w-lg text-sm text-destructive">{message}</p>
        <Button variant="outline" onClick={() => signOut(auth)}>
          Sign out
        </Button>
      </div>
    );
  }

  if (!isViewer) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-5 bg-bg">
        <h1 className="font-serif text-[28px] font-semibold">Squint team members only.</h1>
        <p className="text-text-secondary">
          Signed in as {user?.email ?? "unknown"}. This account isn't a Squint team member.
        </p>
        <Button variant="outline" onClick={() => signOut(auth)}>
          Sign out
        </Button>
      </div>
    );
  }

  return <>{children}</>;
}
